import { useCallback, useEffect, useRef, useState } from 'react';

/** ONE PRESS, EVERY ACTION BUTTON (ported 2026-10-18 from the medrec console's speed-1018 lane, its PR #176, lib/press.ts; the consoles stay
 *  matched, Rod 10-11). A press used to grey its button and say nothing until the server answered, so a slow save looked like no save:
 *   - the button says it is working on the very click: disabled, `aria-busy`, a small spinner and its own words as "-ing" ("Saving",
 *     "Sending LOR", "Working" for a word the console does not know), set in the click's own render, so well inside 100 ms; both labels sit
 *     in one grid cell, so the button keeps the wider one's width and nothing beside it moves;
 *   - a second press while the first is out sends nothing: the guard is a closure flag set before React draws the disabled button, so even
 *     two clicks (or Enter twice) inside one frame send once;
 *   - the press's failure is the caller's (it rethrows), so each caller keeps its own plain words for a refusal.
 *  `PressButton` (press-button.tsx) draws it; every button that writes (a typed fact, Send LOR, Approve, Flag for Rod, Chatter, the dry-run
 *  buttons, Save and file), Try again and Refresh press alike. */

/** THE RING'S TIMING (press.css `press-spin-in`): it shows once a press has taken `PRESS_RING_DELAY_MS`, and once shown the press holds it
 *  `PRESS_RING_HOLD_MS` so it never blinks; a press that answers sooner never shows it and is released at once. */
export const PRESS_RING_DELAY_MS = 150;
export const PRESS_RING_HOLD_MS = 300;
/** How long a press that took `ms` stays busy after its answer. `ringAt`: when its ring shows (0 for a button in a row's tight slot, whose
 *  busy face is the ring alone, shown at once; press-button.tsx `ringOnly`), so that ring too is held its 300 ms and never blinks. */
export const pressHoldMs = (ms: number, ringAt = PRESS_RING_DELAY_MS): number => (ms >= ringAt && ms < ringAt + PRESS_RING_HOLD_MS ? ringAt + PRESS_RING_HOLD_MS - ms : 0);

/** EACH VERB A CONSOLE BUTTON STARTS WITH, as it reads while running (the medrec console's table, its lib/no-confirm.ts, plus this console's
 *  own first words: Post, Text, Fix, Enter). A word not listed reads "Working", never a guessed form. */
const VERBS: Readonly<Record<string, string>> = Object.freeze({
  accept: 'Accepting', add: 'Adding', answer: 'Answering', approve: 'Approving', ask: 'Asking', cancel: 'Cancelling', close: 'Closing', confirm: 'Confirming',
  copy: 'Copying', deny: 'Denying', enter: 'Entering', file: 'Filing', fix: 'Fixing', mark: 'Marking', open: 'Opening', post: 'Posting', put: 'Putting',
  record: 'Recording', remove: 'Removing', resend: 'Resending', save: 'Saving', send: 'Sending', set: 'Setting', text: 'Texting', try: 'Trying', write: 'Writing',
});

/** What a busy button says beside its spinner: its own words with the first verb (and a second after "and") as "-ing" ("Save and file" reads
 *  "Saving and filing"), else "Working". No ellipsis: the spinner says it is under way (the medrec console's DESIGN_SYSTEM rule 6). */
export function pressWordsOf(label: string): string {
  const words = label.trim().split(/\s+/);
  const first = VERBS[(words[0] ?? '').toLowerCase()];
  if (first === undefined) return 'Working';
  const out = [first, ...words.slice(1)];
  const and = out.findIndex((w, i) => i > 0 && w.toLowerCase() === 'and');
  const second = and > 0 ? VERBS[(out[and + 1] ?? '').toLowerCase()] : undefined;
  if (second !== undefined) out[and + 1] = second.toLowerCase();
  return out.join(' ');
}

/** THE PRESS'S GUARD AND CLOCK, without React (the hook below holds one; the unit test drives one): runs `act` once, and a call while one is
 *  out returns false at once and runs nothing. `busy(true)` on the press, `busy(false)` once it settled and held (`pressHoldMs`). */
export function pressGate(busy: (on: boolean) => void, sleep: (ms: number) => Promise<void> = (ms) => new Promise((r) => setTimeout(r, ms)),
  clock: () => number = Date.now, ringAt = PRESS_RING_DELAY_MS): (act: () => Promise<unknown> | unknown) => Promise<boolean> {
  let out = false;
  return async (act) => {
    if (out) return false;
    out = true;
    busy(true);
    const t0 = clock();
    try {
      await act();
    } finally {
      const hold = pressHoldMs(clock() - t0, ringAt);
      if (hold > 0) await sleep(hold);
      out = false;
      busy(false);
    }
    return true;
  };
}

export interface Press {
  /** True from the click until the action settles. */
  busy: boolean;
  /** Runs the action once; a press while one is out returns at once and runs nothing. Rethrows the action's failure. */
  run: () => Promise<void>;
}

/** THE PRESS (see above): `act` is read at the press, so it may close over the latest props and state. `ringNow`: the ring shows from the
 *  click (a row's tight slot, press-button.tsx `ringOnly`), so it is held 300 ms from the click. */
export function usePress(act: () => Promise<unknown> | unknown, { ringNow = false }: { ringNow?: boolean } = {}): Press {
  const latest = useRef(act);
  latest.current = act;
  const live = useRef(true);
  const [busy, setBusy] = useState(false);
  useEffect(() => { live.current = true; return () => { live.current = false; }; }, []);
  const gate = useRef<ReturnType<typeof pressGate> | null>(null);
  gate.current ??= pressGate((on) => { if (on || live.current) setBusy(on); }, undefined, undefined, ringNow ? 0 : PRESS_RING_DELAY_MS);
  const run = useCallback(async (): Promise<void> => { await gate.current!(() => latest.current()); }, []);
  return { busy, run };
}
